
const DB_NAME = 'EditorDrafts';
const DB_VERSION = 3;
const STORE_NAME = 'drafts';
const TITLE_OVERRIDES_STORE_NAME = 'titleOverrides';

export interface TitleOverride {
  id: string;
  articleTitle?: string;
  presetName?: string;
  updatedAt: number;
}

export interface LocalDraft {
  id: string;
  outputContent: string;
  articleTitle: string;
  presetName: string;
  isWeekend?: boolean;
  category?: string;
  isRegistered?: boolean;
  agentName?: string;
  notionPageId?: string;
  createdAt: number;
  updatedAt: number;
}

class LocalDbService {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    if (this.db) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(TITLE_OVERRIDES_STORE_NAME)) {
          db.createObjectStore(TITLE_OVERRIDES_STORE_NAME, { keyPath: 'id' });
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve();
      };

      request.onerror = (event) => {
        console.error('IndexedDB open error:', (event.target as IDBOpenDBRequest).error);
        reject((event.target as IDBOpenDBRequest).error);
      };
    });
  }

  async saveDraft(
    id: string, 
    outputContent: string, 
    articleTitle: string, 
    presetName: string,
    metadata?: { isWeekend?: boolean; category?: string; isRegistered?: boolean; agentName?: string; createdAt?: number, updatedAt?: number, notionPageId?: string }
  ): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('DB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const draft: LocalDraft = {
        id,
        outputContent,
        articleTitle,
        presetName,
        isWeekend: metadata?.isWeekend,
        category: metadata?.category,
        isRegistered: metadata?.isRegistered,
        agentName: metadata?.agentName,
        notionPageId: metadata?.notionPageId,
        createdAt: metadata?.createdAt || Date.now(),
        updatedAt: metadata?.updatedAt || Date.now(),
      };

      const request = store.put(draft);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getDraft(id: string): Promise<LocalDraft | null> {
    await this.init();
    if (!this.db) throw new Error('DB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllDrafts(): Promise<LocalDraft[]> {
    await this.init();
    if (!this.db) throw new Error('DB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteDraft(id: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('DB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async cleanupOldDrafts(maxAgeMs: number): Promise<number> {
    await this.init();
    if (!this.db) throw new Error('DB not initialized');

    const cutoff = Date.now() - maxAgeMs;
    const drafts = await this.getAllDrafts();
    const oldDrafts = drafts.filter(d => (d.createdAt || d.updatedAt) < cutoff);

    if (oldDrafts.length === 0) return 0;

    console.log(`[LocalDbService] Cleaning up ${oldDrafts.length} old drafts...`);

    return new Promise((resolve, reject) => {
      // Only touch the drafts store. titleOverrides are kept permanently so
      // edited titles and preset names are not lost.
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      let deletedCount = 0;
      oldDrafts.forEach(draft => {
        store.delete(draft.id);
        deletedCount++;
      });

      transaction.oncomplete = () => resolve(deletedCount);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async saveTitleOverride(
    id: string,
    updates: { articleTitle?: string; presetName?: string; updatedAt?: number }
  ): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('DB not initialized');

    const existing = await this.getTitleOverride(id);
    const override: TitleOverride = {
      id,
      articleTitle: updates.articleTitle !== undefined ? updates.articleTitle : existing?.articleTitle,
      presetName: updates.presetName !== undefined ? updates.presetName : existing?.presetName,
      // Guard against 0/invalid timestamps so the override always wins over createdAt.
      updatedAt: (updates.updatedAt && updates.updatedAt > 0) ? updates.updatedAt : Date.now(),
    };

    // Nothing to persist if both title fields are empty (undefined or blank).
    const hasArticleTitle = override.articleTitle !== undefined && override.articleTitle !== '';
    const hasPresetName = override.presetName !== undefined && override.presetName !== '';
    if (!hasArticleTitle && !hasPresetName) {
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([TITLE_OVERRIDES_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(TITLE_OVERRIDES_STORE_NAME);
      const request = store.put(override);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getTitleOverride(id: string): Promise<TitleOverride | null> {
    await this.init();
    if (!this.db) throw new Error('DB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([TITLE_OVERRIDES_STORE_NAME], 'readonly');
      const store = transaction.objectStore(TITLE_OVERRIDES_STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllTitleOverrides(): Promise<TitleOverride[]> {
    await this.init();
    if (!this.db) throw new Error('DB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([TITLE_OVERRIDES_STORE_NAME], 'readonly');
      const store = transaction.objectStore(TITLE_OVERRIDES_STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteTitleOverride(id: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('DB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([TITLE_OVERRIDES_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(TITLE_OVERRIDES_STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearAll(): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('DB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME, TITLE_OVERRIDES_STORE_NAME], 'readwrite');
      const draftsStore = transaction.objectStore(STORE_NAME);
      const overridesStore = transaction.objectStore(TITLE_OVERRIDES_STORE_NAME);
      draftsStore.clear();
      overridesStore.clear();

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

export const localDbService = new LocalDbService();
